import { AppCrashFallback } from 'components/AppCrashFallback/AppCrashFallback';
import { CustomerAgreementDialog } from 'components/CustomerAgreementDialog/CustomerAgreementDialog';
import { ReactElement, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useBoolean } from 'react-use';
import { shouldSignLatestTermsOfAgreement } from 'redux/User/selectors';
import { AuthorizationConfig } from 'redux/User/types';
import { useAuthorizationService } from 'redux/User/useAuthorizationService';
import { requestLoginRedirect } from 'services/authentication/loginRedirect';
import { MSAL_REDIRECT_PARAMETERS } from 'services/authentication/msalConfig';
import { useAuthenticationStatus } from 'services/authentication/useAuthenticationStatus';

import { ROUTES } from './routes';

type Props = AuthorizationConfig & {
  children: ReactElement;
};

export const ProtectedRoute = ({
  children,
  applications,
  roles,
  isAllowedForInternalCompany,
}: Props) => {
  const { isLoading, isAuthenticated } = useAuthenticationStatus();
  const isUserAuthorized = useAuthorizationService()({
    applications,
    roles,
    isAllowedForInternalCompany,
  });
  const shouldOpenCustomerAgreementDialog = useSelector(shouldSignLatestTermsOfAgreement);
  const [hasAcceptedAgreements, setHasAcceptedAgreements] = useBoolean(false);
  const [hasRedirectFailed, setHasRedirectFailed] = useBoolean(false);

  // Redirecting from an effect (and not from the render body) keeps MSAL from
  // being called again on every re-render that happens while the redirect is
  // still being prepared, which throws `interaction_in_progress`.
  useEffect(() => {
    if (isLoading || isAuthenticated) {
      return;
    }

    let isStale = false;

    requestLoginRedirect(MSAL_REDIRECT_PARAMETERS).then((hasStarted) => {
      // No navigation is coming, so show an error rather than a blank page.
      if (!hasStarted && !isStale) {
        setHasRedirectFailed(true);
      }
    });

    return () => {
      isStale = true;
    };
  }, [isLoading, isAuthenticated, setHasRedirectFailed]);

  if (hasRedirectFailed) {
    return <AppCrashFallback eventId="" />;
  }

  if (isLoading || !isAuthenticated) {
    return <></>;
  }

  if (!isUserAuthorized) {
    return <Navigate to={ROUTES.NOT_FOUND} replace />;
  }

  return (
    <>
      {children}
      <CustomerAgreementDialog
        isOpen={shouldOpenCustomerAgreementDialog && !hasAcceptedAgreements}
        closeCustomerAgreementDialog={() => setHasAcceptedAgreements(true)}
      />
    </>
  );
};
